import { ReactElement, useMemo } from 'react';

import { AuraNextMedicalInterviewConfig } from '@breathelife/auranext';
import config from '@breathelife/config/frontend';
import featureFlag, { FeatureFlag } from '@breathelife/feature-flag';
import { EnabledTabs, LeadPlatform, LeadStatusesColumnData } from '@breathelife/lead-platform';
import {
  Auth0Config,
  CloudinaryConfig,
  ConsumerFeedbackProvider,
  CountryCode,
  SimplesatSurveysConfig,
  EmailNotificationTypes,
  EnforceUniqueEmailStrategy,
  ESignSignerAuthMethod,
  Language,
  LaunchApplicationType,
  LeadPlatformHeaderVariants,
  LeadStatus,
  LeadTableColumnInfo,
  LineOfBusinessName,
  MedicalInterviewProvider,
  NodeIdAgentLicenseConfig,
  PaymentServiceProvider,
  ProductsWidgetFeatureType,
  QuestionnaireVersionFlag,
  SignatureType,
} from '@breathelife/types';
import { NodeId, QuestionnaireScreensConfig } from '@zinnia/form-engine-sdk';

const isEmailActionEnabled = (leadStatus: LeadStatus): boolean => {
  const enabledStatuses: LeadStatus[] = config.get<LeadStatus[]>('features.leadsPlatform.emails.enabledStatuses');
  return enabledStatuses.includes(leadStatus);
};

function LeadPlatformContainer(): ReactElement {
  const leadPlatformTextOverrides = config.get<Partial<Record<Language, any>>>('localization.leadPlatform');
  const tableColumns = useMemo(() => {
    return config.get<LeadTableColumnInfo[]>('features.leadsPlatform.table.columns');
  }, []);
  const fileUploadsAllowedLinesOfBusiness = config.get<LineOfBusinessName[]>(
    'features.fileUpload.allowedLinesOfBusiness',
  );

  const fileUploadMaxFileSizeInMB = config.get<number>('features.fileUpload.maxFileSizeInMB');

  const enabledTwoFactorAuthenticationTypes = config.get<ESignSignerAuthMethod[]>(
    'features.eSignature.enabledTwoFactorAuthenticationTypes',
  );
  return (
    <LeadPlatform
      carrierInfo={{
        companyName: config.get<string>('carrier.companyName.en'),
        id: config.get<string>('carrier.id'),
        validFileUploadMimeTypes: config.get('carrier.validFileUploadMimeTypes'),
      }}
      config={{
        auth0: config.get<Auth0Config>('keys.auth0'),
        cloudinary: config.get<CloudinaryConfig>('keys.cloudinary'),
        countryCode: config.get<CountryCode>('carrier.defaultCountryCode'),
        git: {
          commitSha: config.get<string>('git.commitSha'),
        },
        googleMapsPlaces: config.get<string>('keys.googleMapsPlaces'),
      }}
      enabledTabs={config.get<EnabledTabs>('features.leadsPlatform.drawer.tabs')}
      isEmailActionEnabled={isEmailActionEnabled}
      headerVariant={config.get<LeadPlatformHeaderVariants>('carrier.headerVariant')}
      leadStatuses={config.get<LeadStatusesColumnData>('features.leadsPlatform.statuses')}
      leadTableColumns={tableColumns}
      mgas={[]}
      monitoringEnvironment={config.get<string>('monitoring.environment')}
      textOverrides={leadPlatformTextOverrides}
      features={{
        agentLicenseValidation: {
          enabled: config.get<boolean>('featureFlags.enableAgentLicenseValidation'),
        },
        applicationCopy: {
          disabledForLineOfBusiness: config.get<LineOfBusinessName[] | undefined>(
            'features.applicationCopy.disabledForLineOfBusiness',
          ),
          applicationCopyThresholdInDays: config.get<number>('features.applicationCopy.applicationCopyThresholdInDays'),
          enabled: config.get<boolean>('features.applicationCopy.enabled'),
          startDate: config.get<string>('features.applicationCopy.startDate'),
        },
        enableAllowingCopyingSubmittedApplication: {
          enabled: featureFlag.get(FeatureFlag.enableAllowingCopyingSubmittedApplication),
        },
        applicationMarketingDataExtract: {
          enabled: config.get<boolean>('featureFlags.enableApplicationMarketingDataExtract'),
        },
        applicationSupportAssociatedFiles: {
          enabled: config.get<boolean>('featureFlags.enableApplicationSupportAssociatedFiles'),
        },
        applicationSupportTools: {
          enabled: featureFlag.get(FeatureFlag.enableApplicationSupportTools),
        },
        assistedApplication: {
          adoMinMax: {
            enabled: config.get<boolean>('features.adoMinMax.enabled'),
            adoOptionNodeId: config.get<string>('features.nodeIds.ado.option'),
            adoCoverageAmount: config.get<string>('features.nodeIds.ado.coverageAmount'),
          },
          documentsDrawer: {
            enabled: config.get<boolean>('featureFlags.enableDocumentsDrawer'),
          },
          equivalentSingleAge: {
            enabled: config.get<boolean>('features.equivalentSingleAge.enabled'),
            jointProducts: config.get<string[]>('features.equivalentSingleAge.jointProducts'),
            selectedProductNodeId: config.get<string>('features.nodeIds.equivalentSingleAge.selectedProduct'),
            insuredInfoNodeIds: config.get<string[]>('features.nodeIds.equivalentSingleAge.insuredInfo'),
          },
          jetDecisionWidget: {
            enabled: config.get<boolean>('features.jetDecisionWidget.enabled'),
          },
          nodeIdAgentLicenseConfig: config.get<NodeIdAgentLicenseConfig>('features.nodeIds.agentLicense'),
          nodeIdsAffectingPricing: (config.get('features.nodeIds.pricing') || []) as NodeId[],
          nodeIdsAffectingParticipantGeneration: (config.get('features.nodeIds.participantGeneration') ||
            []) as NodeId[],
          nodeIdsAffectingParticipantMedicalInterview: (config.get('features.nodeIds.participantMedicalInterview') ||
            []) as NodeId[],
          nodeIdsAffectingProducts: (config.get('features.nodeIds.product') || []) as NodeId[],
          nodeIdsApplicantFullNamePerLineOfBusiness: {
            [LineOfBusinessName.life]: {
              firstName: config.get<NodeId>(`features.nodeIds.applicantFullName.${LineOfBusinessName.life}.firstName`),
              lastName: config.get<NodeId>(`features.nodeIds.applicantFullName.${LineOfBusinessName.life}.lastName`),
            },
            [LineOfBusinessName.annuity]: {
              firstName: config.get<NodeId>(
                `features.nodeIds.applicantFullName.${LineOfBusinessName.annuity}.firstName`,
              ),
              lastName: config.get<NodeId>(`features.nodeIds.applicantFullName.${LineOfBusinessName.annuity}.lastName`),
            },
            [LineOfBusinessName.incomeProtection]: {
              firstName: config.get<NodeId>(
                `features.nodeIds.applicantFullName.${LineOfBusinessName.incomeProtection}.firstName`,
              ),
              lastName: config.get<NodeId>(
                `features.nodeIds.applicantFullName.${LineOfBusinessName.incomeProtection}.lastName`,
              ),
            },
            [LineOfBusinessName.accidentIllness]: {
              firstName: config.get<NodeId>(
                `features.nodeIds.applicantFullName.${LineOfBusinessName.accidentIllness}.firstName`,
              ),
              lastName: config.get<NodeId>(
                `features.nodeIds.applicantFullName.${LineOfBusinessName.accidentIllness}.lastName`,
              ),
            },
            [LineOfBusinessName.disability]: {
              firstName: '',
              lastName: '',
            },
            [LineOfBusinessName.health]: {
              firstName: '',
              lastName: '',
            },
            [LineOfBusinessName.longTermCare]: {
              firstName: '',
              lastName: '',
            },
            [LineOfBusinessName.medicalSupplement]: {
              firstName: '',
              lastName: '',
            },
            [LineOfBusinessName.criticalIllness]: {
              firstName: '',
              lastName: '',
            },
          },
          nodeIdsSkippingDebounce: config.get<boolean>('features.applicationCopy.enabled')
            ? (config.get('features.nodeIds.noDebounce') as NodeId[])
            : [],
          productsWidget: {
            enabled: config.get<boolean>('features.productsWidget.enabled'),
            allowedLinesOfBusiness: config.get<LineOfBusinessName[]>('features.productsWidget.allowedLinesOfBusiness'),
            type: config.get<ProductsWidgetFeatureType>('features.productsWidget.type'),
          },
          fileUpload: {
            maxFileSizeInMB: fileUploadMaxFileSizeInMB,
            ...(fileUploadsAllowedLinesOfBusiness && { allowedLinesOfBusiness: fileUploadsAllowedLinesOfBusiness }),
          },
        },
        auditLogs: {
          enabled: config.get<boolean>('featureFlags.enableAuditLogs'),
        },
        consumerFeedback: {
          enabled: config.get<boolean>('features.consumerFeedback.enabled'),
          provider: config.get<ConsumerFeedbackProvider>('features.consumerFeedback.provider'),
          simplesat: config.get<SimplesatSurveysConfig>('features.consumerFeedback.simplesat'),
        },
        documentTemplate: {
          enabled: config.get<boolean>('featureFlags.enableDocumentTemplateFeature'),
        },
        enableInstantIdRequest: {
          enabled: config.get<boolean>('featureFlags.enableInstantIdRequest'),
        },
        eSignature: {
          enabledTwoFactorAuthenticationTypes,
          enforceUniqueEmail: config.get<EnforceUniqueEmailStrategy>('features.eSignature.enforceUniqueEmail'),
          inPerson: config.get<boolean>('features.eSignature.inPersonSignature.enabled'),
          editCeremony: config.get<boolean>('features.eSignature.editCeremony.enabled'),
        },
        importCopyDeck: {
          enabled: config.get<boolean>('featureFlags.enableImportFromCopyDeck'),
        },
        launchApplication: {
          enabled: config.get<boolean>('features.launchApplication.enabled'),
          signatureType: config.get<SignatureType>('features.signatureType.leadPlatform'),
          type: config.get<LaunchApplicationType>('features.launchApplication.type'),
        },
        leadCreation: {
          enabled: config.get<boolean>('features.leadsPlatform.leadCreation.enabled'),
        },
        hideCreateButton: {
          enabled: config.get<boolean>('features.leadsPlatform.hideCreateButton.enabled'),
        },
        loadCarrierNamesFromDb: {
          enabled: config.get<boolean>('featureFlags.enableLoadCarrierNamesFromDb'),
        },
        loadPublicLinkMetadataFromDb: {
          enabled: config.get<boolean>('featureFlags.enableLoadPublicLinkMetadataFromDb'),
        },
        medicalInterview: {
          enabled: config.get<boolean>('features.medicalInterview.enabled'),
          provider: config.get<MedicalInterviewProvider>('features.medicalInterview.provider'),
          auraNext: config.get<AuraNextMedicalInterviewConfig | undefined>('features.medicalInterview.auraNext'),
        },
        multiBranding: {
          enabled: config.get<boolean>('features.multiBranding.enabled'),
        },
        multiQuestionnaire: {
          enabled: config.get<boolean>('features.multiQuestionnaire.enabled'),
        },
        needsAnalysis: {
          enabled: config.get<boolean>('features.needsAnalysis.enabled'),
          optional: config.get<boolean>('featureFlags.enableOptionalNeedsAnalysis'),
        },
        notificationCentre: {
          enabled: config.get<boolean>('features.notificationCentre.enabled'),
          maxDays: config.get<number>('features.notificationCentre.maxDays'),
          cloudinaryImages: config.get<{
            informational: { en: string; fr: string };
            actionable: { en: string; fr: string };
          }>('features.notificationCentre.cloudinaryImages'),
        },
        underwritingRequirements: {
          enabled: config.get<boolean>('features.underwritingRequirements.enabled'),
          allowedLinesOfBusiness: config.get<LineOfBusinessName[]>(
            'features.underwritingRequirements.allowedLinesOfBusiness',
          ),
        },
        payments: {
          enabled: config.get<boolean>('features.payments.enabled'),
          serviceProvider: config.get<PaymentServiceProvider>('features.payments.serviceProvider'),
          [config.get<PaymentServiceProvider>('features.payments.serviceProvider')]: config.get(
            `features.payments.${config.get<PaymentServiceProvider>('features.payments.serviceProvider')}`,
          ),
        },
        pointOfSaleDecisionOutcome: {
          enabled: config.get<boolean>('features.pointOfSaleDecisionOutcome.enabled'),
        },
        pricingCalculation: {
          enabled: config.get<boolean>('featureFlags.enableLoadingPricingNodeIdsFromDB'), // TODO HOT-4320: Replace it with feature once this is tested and released
        },
        incompleteFieldsSupport: {
          enabled: config.get<boolean>('features.incompleteFieldsSupport.enabled'),
        },
        productHub: {
          enabled: config.get<boolean>('features.productHub.enabled'),
        },
        questionnaireBuilder: {
          enabled: config.get<boolean>('featureFlags.enableQuestionnaireBuilder'),
        },
        questionnaireDebugTools: {
          enabled: config.get<boolean>('debug.displayQuestionnaireFillerToolbar'),
        },
        questionnaireEditor: {
          enabled: config.get<boolean>('featureFlags.enableQuestionnaireEditor'),
        },
        questionnairePreview: {
          enabled: config.get<boolean>('featureFlags.enableQuestionnairePreview'),
        },
        questionnaireSchemaExport: {
          enabled: config.get<boolean>('featureFlags.enableQuestionnaireSchemaExport'),
        },
        questionnaireScreen: {
          enabled: config.get<boolean>('features.questionnaireScreen.enabled'),
          config: config.get<QuestionnaireScreensConfig>('features.questionnaireScreen.config'),
        },
        questionnaireVersionFlags: {
          enabled: config.get<boolean>('features.questionnaireVersionFlags.enabled'),
          flags: config.get<QuestionnaireVersionFlag[]>('features.questionnaireVersionFlags.flags'),
        },
        requireSalesDecisionRuleIdentifier: {
          enabled: config.get<boolean>('features.requireSalesDecisionRuleIdentifier.enabled'),
        },
        downloadSubmissionFiles: {
          enabled: featureFlag.get(FeatureFlag.enableDownloadSubmissionFiles),
        },
        salesDecisionRules: {
          enabled: config.get<boolean>('featureFlags.enableSalesDecisionRules'),
        },
        showBlueprintIdInEditor: {
          enabled: config.get<boolean>('features.engine.showBlueprintIdInEditor'),
        },
        signatureType: config.get<SignatureType>('features.signatureType.leadPlatform'),
        softwareByWidget: {
          enabled: config.get<boolean>('features.softwareByWidget.enabled'),
        },
        thirdPartyIntegrations: {
          enabled: config.get<boolean>('featureFlags.enableThirdPartyIntegrations'),
        },
        userNotificationEmail: {
          enabled: config.get<boolean>('features.userNotificationEmail.enabled'),
          emailTypes: config.get<EmailNotificationTypes[]>('features.userNotificationEmail.emailTypes'),
        },
        userSecurityDisclosure: {
          enabled: config.get<boolean>('featureFlags.enableUserSecurityDisclosure'),
        },
        cookieConsent: {
          enabled: config.get<boolean>('features.cookieConsent.enabled'),
        },
        announcements: {
          enabled: config.get<boolean>('features.announcements.enabled'),
        },
      }}
    />
  );
}

export default LeadPlatformContainer;
